import type { ViewportEntity } from './types';
import * as helpers from '../../ParseHelpers';
import parsePoint from '../../shared/parsePoint';
import { CommonDxfEntity } from '../shared';
import { ensurePoint3D } from '../../../utils';

function parseViewport(entity: ViewportEntity, scanner: any, curr: any): boolean {
    if (curr === 'EOF') return false;

    // Create a copy of the entity to avoid direct modification
    const updatedEntity = { ...entity };

    switch (curr.code) {
        case 0:
            return false;
        case 100:
            updatedEntity.subclassMarker = curr.value;
            break;
        case 10:
            updatedEntity.viewportCenter = ensurePoint3D(parsePoint(scanner));
            break;
        case 40:
            updatedEntity.width = curr.value;
            break;
        case 41:
            updatedEntity.height = curr.value;
            break;
        case 68:
            updatedEntity.status = curr.value;
            break;
        case 69:
            updatedEntity.viewportId = curr.value;
            break;
        case 12:
            updatedEntity.displayCenter = parsePoint(scanner);
            break;
        case 13:
            updatedEntity.snapBase = parsePoint(scanner);
            break;
        case 14:
            updatedEntity.snapSpacing = parsePoint(scanner);
            break;
        case 15:
            updatedEntity.gridSpacing = parsePoint(scanner);
            break;
        case 16:
            updatedEntity.viewDirection = ensurePoint3D(parsePoint(scanner));
            break;
        case 17:
            updatedEntity.targetPoint = ensurePoint3D(parsePoint(scanner));
            break;
        case 42:
            updatedEntity.perspectiveLensLength = curr.value;
            break;
        case 43:
            updatedEntity.frontClipZ = curr.value;
            break;
        case 44:
            updatedEntity.backClipZ = curr.value;
            break;
        case 45:
            updatedEntity.viewHeight = curr.value;
            break;
        case 50:
            updatedEntity.snapAngle = curr.value;
            break;
        case 51:
            updatedEntity.viewTwistAngle = curr.value;
            break;
        case 72:
            updatedEntity.circleZoomPercent = curr.value;
            break;
        case 331:
            updatedEntity.frozenLayerIds = updatedEntity.frozenLayerIds || [];
            updatedEntity.frozenLayerIds.push(curr.value);
            break;
        case 90:
            updatedEntity.statusBitFlags = curr.value;
            break;
        case 340:
            updatedEntity.clippingBoundaryId = curr.value;
            break;
        case 1:
            updatedEntity.sheetName = curr.value;
            break;
        case 281:
            updatedEntity.renderMode = curr.value;
            break;
        case 71:
            updatedEntity.ucsPerViewport = curr.value;
            break;
        case 110:
            updatedEntity.ucsOrigin = ensurePoint3D(parsePoint(scanner));
            break;
        case 111:
            updatedEntity.ucsXAxis = ensurePoint3D(parsePoint(scanner));
            break;
        case 112:
            updatedEntity.ucsYAxis = ensurePoint3D(parsePoint(scanner));
            break;
        case 345:
            updatedEntity.ucsId = curr.value;
            break;
        case 346:
            updatedEntity.ucsBaseId = curr.value;
            break;
        case 79:
            updatedEntity.orthographicType = curr.value;
            break;
        case 146:
            updatedEntity.elevation = curr.value;
            break;
        case 170:
            updatedEntity.shadePlotMode = curr.value;
            break;
        case 61:
            updatedEntity.majorGridFrequency = curr.value;
            break;
        case 332:
            updatedEntity.backgroundId = curr.value;
            break;
        case 333:
            updatedEntity.shadePlotId = curr.value;
            break;
        case 348:
            updatedEntity.visualStyleId = curr.value;
            break;
        case 292:
            updatedEntity.isDefaultLighting = !!curr.value;
            break;
        case 282:
            updatedEntity.defaultLightingType = curr.value;
            break;
        case 141:
            updatedEntity.brightness = curr.value;
            break;
        case 142:
            updatedEntity.contrast = curr.value;
            break;
        case 63:
        case 421:
        case 431:
            updatedEntity.ambientLightColor = curr.value;
            break;
        case 361:
            updatedEntity.sunId = curr.value;
            break;
        case 335:
        case 343:
        case 344:
        case 91:
            updatedEntity.softPointer = curr.value;
            break;
        default:
            // Fall back to common property parsing
            return false;
    }

    // Return the modified object
    return true;
}

export default class ViewportParser {
    static ForEntityName = 'VIEWPORT';

    static parseEntity(scanner: any, current: any): ViewportEntity {
        const entity: ViewportEntity = {} as ViewportEntity;
        let curr = current;

        while (curr !== 'EOF') {
            if (curr.code === 0) {
                scanner.rewind();
                break;
            }

            const parsed = parseViewport(entity, scanner, curr);
            if (!parsed) {
                helpers.checkCommonEntityProperties(entity as CommonDxfEntity, curr, scanner);
            }

            curr = scanner.next();
        }

        return entity;
    }
}
